import type { EditorJsEditorHandle } from './editor-js-editor-handle';
import type { Ref, RefObject } from 'react';

import { useImperativeHandle } from 'react';

import type { EditorJsData } from '#pages/document/editorjs-types';

export function useEditorJsImperativeHandle(
  ref: Ref<EditorJsEditorHandle | null>,
  {
    editorRef,
    persistChainRef,
    latestValueRef,
    schedulePersist,
    commitEditorOutput,
  }: {
    editorRef: RefObject<any | null>;
    persistChainRef: RefObject<Promise<unknown>>;
    latestValueRef: RefObject<EditorJsData | null>;
    schedulePersist: (run: () => Promise<void>) => Promise<unknown>;
    commitEditorOutput: (data: EditorJsData) => void;
  },
): void {
  useImperativeHandle(
    ref,
    () => ({
      getValue: () => latestValueRef.current,
      resetContent: (data: EditorJsData | null) => {
        persistChainRef.current = Promise.resolve();
        latestValueRef.current = data ?? { blocks: [] };
        const editor = editorRef.current;
        if (!editor) return;
        void editor.isReady
          .then(() =>
            editor.render((latestValueRef.current as any) ?? { blocks: [] }),
          )
          .catch(() => {});
      },
      flushSave: async () => {
        const editor = editorRef.current;
        if (!editor) return;
        await schedulePersist(async () => {
          const data = (await editor.save()) as EditorJsData;
          commitEditorOutput(data);
        });
      },
    }),
    [commitEditorOutput, schedulePersist],
  );
}
